import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { MSelectCard } from "@/components/ui/atoms/MSelectCard";
import styles from "../reminder.module.css";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { getMemberInfo } from "@/hooks/useMembers";
import { MButton } from "@/components/ui/atoms/MButtons";
import CriticalReminderToggle from "@/components/ui/molecules/CriticalReminderToggle";
import { useReminders } from "@/hooks/useReminders";
import {
  CreateReminder,
  DayFrequency,
  ReminderFrequency,
  Reminders,
  ReminderType,
  UpdateReminder,
} from "@/types/reminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import {
  displayDayFrequencyField,
  displayEventForField,
  displayFrequencyField,
  getMedicationFrequencyText,
  paintDayFrequencyField,
  paintDosageInput,
  paintDropDownFrequencySelectField,
  paintFrequencyDaysField,
  paintFrequencyDisplayField,
  paintFrequencySpecificDaysField,
  paintReminderDateField,
  paintReminderEndDateField,
  paintReminderNotesField,
  paintReminderTimeField,
  ReminderInputStage,
} from "../remindersHelper";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { useMedicines } from "@/hooks/useMedicine";
import { useMemberMedications } from "@/hooks/member-medication.hook";
import MLoader from "@/components/ui/atoms/MLoader";
import { IMedication } from "@/types/medication";
import { DEFAULT_TIME_12HR, DEFAULT_YEARS_AHEAD } from "@/utils/constants";
import {
  getTimezone,
  initDate,
  isTimeBefore,
  updateTime,
} from "@/utils/date-utils";
import { calculateOptimalDosages } from "@/utils/dosage-scheduler";
import MFrame from "@/components/ui/atoms/MFrame";
import { getErrorMessage } from "@/utils/error";
import { addYears, isBefore } from "date-fns";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { MAccordionHeader } from "@/components/ui/atoms/MAccordionHeader";
import { useKeyboardScrollToTop } from "@/hooks/useKeyboardScrollToTop";
import { useFixMobileKeyboard } from "@/hooks/useFixMobileKeyboard";
import { IconFrequency } from "@/components/icons";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { IconFileOff } from "@tabler/icons-react";
import { IconPillFilled } from "@tabler/icons-react";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

/**
 * Insurance Reminder Creation Page
 * Allows users to select an insurance type to create a reminder for
 */
const MedicationReminder: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;

  // Get reminder data for editing - memoize to prevent unexpected changes
  const reminder: Reminders = location.state?.reminder;
  const isEditing = useMemo(() => {
    const result = !!reminder;
    return result;
  }, [reminder]);

  // Initialize state with reminder data if editing
  const [reminderFor, setReminderFor] = useState<string | null>(
    reminder?.reminderFor ?? null
  );
  const [stage, setStage] = useState<ReminderInputStage>(
    isEditing ? ReminderInputStage.ADD : ReminderInputStage.START
  );
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);
  const [frequency, setFrequency] = useState<ReminderFrequency | null>(
    reminder?.frequency ?? null
  );
  const [frequencyValue, setFrequencyValue] = useState<number | null>(
    reminder?.frequencyValue ?? null
  );

  // Initialize dosage times from reminder data if available
  const [firstDosage, setFirstDosage] = useState<string | null>(
    reminder?.dayFrequencyValue && reminder.dayFrequencyValue.length > 0
      ? reminder.dayFrequencyValue[0]
      : null
  );
  const [secondDosage, setSecondDosage] = useState<string | null>(
    reminder?.dayFrequencyValue && reminder.dayFrequencyValue.length > 1
      ? reminder.dayFrequencyValue[1]
      : null
  );
  const [thirdDosage, setThirdDosage] = useState<string | null>(
    reminder?.dayFrequencyValue && reminder.dayFrequencyValue.length > 2
      ? reminder.dayFrequencyValue[2]
      : null
  );
  const [fourthDosage, setFourthDosage] = useState<string | null>(
    reminder?.dayFrequencyValue && reminder.dayFrequencyValue.length > 3
      ? reminder.dayFrequencyValue[3]
      : null
  );
  const [showDayFrequencyDrawer, setShowDayFrequencyDrawer] =
    useState<boolean>(false);
  const [dayFrequency, setDayFrequency] = useState<DayFrequency | null>(
    reminder?.dayFrequency ?? null
  );
  const [reminderDate, setReminderDate] = useState<Date | null>(
    initDate(reminder?.startDate)
  );
  const [reminderEndDate, setReminderEndDate] = useState<Date | null>(
    initDate(reminder?.endDate)
  );
  const [selectedDays, setSelectedDays] = useState<string[]>(
    reminder?.selectedDays?.map((day) => day.toString()) ?? []
  );
  const [showSetDays, setShowSetDays] = useState<boolean>(false);
  const [notes, setNotes] = useState<string | null>(reminder?.notes ?? null);
  const [isCritical, setIsCritical] = useState<boolean>(
    reminder ? reminder.tier === "CRITICAL" : true
  );
  const [stageStack, setStageStack] = useState<ReminderInputStage[]>([
    ReminderInputStage.START,
  ]);
  const [showMedicationAccordion, setShowMedicationAccordion] =
    useState<boolean>(false);
  const { addReminder, updateReminder, totalCount, fetchReminders } =
    useReminders(memberInfo.id);

  // Use keyboard scroll-to-top hook
  // useKeyboardScrollToTop([stage, reminderFor]);

  // Fix mobile keyboard issues
  useFixMobileKeyboard();

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);
  const { searchMedicines, searchResults, searchLoading } = useMedicines();
  const { medications, loading, fetched, fetchMedications } =
    useMemberMedications(memberId);
  const [showFrequencyDrawer, setShowFrequencyDrawer] =
    useState<boolean>(false);

  // Calculate exitPath early - before any early returns (for use in all render paths)
  const exitPath = stage === ReminderInputStage.START ? "" : "/reminders";

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION,
    });
  }, []);

  // First fetch medications
  useEffect(() => {
    const loadData = async () => {
      await fetchMedications();
    };
    if (!fetched) loadData();
  }, [fetched, fetchMedications]);

  const validateCurrentStage = (): boolean => {
    let isValid = true;
    const newErrors: { [key: string]: boolean } = {};

    if (stage === ReminderInputStage.DETAILS) {
      // Validate provider is selected
      if (!reminderFor) {
        newErrors.reminderFor = true;
        isValid = false;
      }
    }

    setValidationErrors(newErrors);
    setShowValidationErrors(!isValid);
    return isValid;
  };

  // Show toast message for error notification
  const showErrorToast = (_: string) => {
    showToast({
      message: t("errors.toast"),
      variant: "error",
    });
  };

  const handleBack = () => {
    // If editing, go back to reminders list directly
    if (isEditing) {
      navigate("/reminders");
      return;
    }

    let nextStage = stageStack.pop();
    if (stage === nextStage) {
      nextStage = stageStack.pop();
    }

    if (stageStack.length === 0) {
      stageStack.push(ReminderInputStage.START);
    }

    if (nextStage !== undefined) {
      setStage(nextStage);
      setStageStack([...stageStack]);
    } else {
      navigate(-1);
    }
  };

  const nextReminderInputStage = (): ReminderInputStage =>
    dayFrequency === DayFrequency.ONCE
      ? ReminderInputStage.REMINDER_DETAILS
      : ReminderInputStage.REMINDER_SCHEDULE;

  const nextStage = () => {
    let nextStage: ReminderInputStage;
    switch (stage) {
      case ReminderInputStage.START:
        nextStage = ReminderInputStage.FREQUENCY_DETAILS;
        break;
      case ReminderInputStage.FREQUENCY_DETAILS:
        switch (frequency) {
          case ReminderFrequency.SPECIFIC_DAYS:
            nextStage = ReminderInputStage.DAY_FREQUENCY;
            break;
          case ReminderFrequency.DAY_FREQUENCY:
            nextStage = ReminderInputStage.DAY_INPUT;
            break;
          default:
            nextStage = nextReminderInputStage();
            break;
        }
        break;
      case ReminderInputStage.DAY_FREQUENCY:
        nextStage = nextReminderInputStage();
        break;
      case ReminderInputStage.DAY_INPUT:
        nextStage = nextReminderInputStage();
        break;
      case ReminderInputStage.REMINDER_SCHEDULE:
        nextStage = ReminderInputStage.REMINDER_DETAILS;
        break;
      case ReminderInputStage.REMINDER_DETAILS:
        nextStage = ReminderInputStage.ADD;
        break;
      default:
        showErrorToast(t("errors.maxStageReached"));
        return;
    }

    if (validateCurrentStage()) {
      stageStack.push(nextStage);
      setStage(nextStage);
      setStageStack([...stageStack]);
      setShowValidationErrors(false);

      // Track stage changes
      if (nextStage === ReminderInputStage.FREQUENCY_DETAILS) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_FREQUENCY,
        });
      } else if (nextStage === ReminderInputStage.REMINDER_SCHEDULE) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_TIME,
        });
      } else if (nextStage === ReminderInputStage.REMINDER_DETAILS) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_DATE,
        });
      } else if (nextStage === ReminderInputStage.ADD) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_SUMMARY,
        });
      }

      // Auto-scroll to top when moving to next stage
      setTimeout(() => {
        document
          .querySelector("main")
          ?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleMedicationSelect = (medication: IMedication) => {
    if (medication) {
      setReminderFor(getReminderFor(medication));
      setFrequency(medication.frequency);
      setSelectedDays(medication.selectedWeekdays);
      setFrequencyValue(
        medication.everyXDays ? Number(medication.everyXDays) : null
      );
      setDayFrequency(medication.dayFrequency);
    }
  };

  const paintMedicationsSelect = () => {
    return (
      <div
        className={`${styles.optionsContainer} ${
          showMedicationAccordion ? styles.optionsContainerCompact : ""
        }`}
      >
        <MAccordionHeader
          title={t("reminders.medication.selectFromList")}
          isOpen={showMedicationAccordion}
          onClick={() => setShowMedicationAccordion(!showMedicationAccordion)}
          className="accordion-header-16"
          titleColor="var(--brand-colours-indigo-indigo-700, #45168C)"
        />
        {showMedicationAccordion && (
          <>
            {medications.length === 0 ? (
              <div className="mt-4">
                <MEmptyCard
                  icon={<IconFileOff size={80} stroke={2} color="#AD95D1" />}
                  primaryTitle={t("reminders.medication.noMedicinesSaved")}
                  secondaryTitle={t(
                    "reminders.medication.noMedicinesSavedDescription"
                  )}
                />
              </div>
            ) : (
              <div
                className={`${styles.optionsContainer} ${styles.optionsContainerCompact} mt-4`}
              >
                {medications.map((option) => (
                  <MSelectCard
                    key={option.id}
                    text={option.name}
                    selected={reminderFor === option.name}
                    onClick={() => handleMedicationSelect(option)}
                    className={styles.optionCard}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const getReminderFor = (item: any) => {
    // Handle both Medicine (medicine_name) and IMedication (name) types
    // Just return the medicine name since it often already contains dosage info
    // The dosage field typically contains chemical composition, not the amount
    return item.medicine_name || item.name;
  };

  const paintTypeAheadInput = () => {
    return (
      <AutoCompleteInput
        label={t("reminders.medication.typeAhead")}
        data={searchResults}
        getOptionLabel={(med) => med.medicine_name}
        renderOption={(med) => (
          <div>
            <div className="font-medium text-gray-900">{med.medicine_name}</div>
            {med.dosage && (
              <div className="text-xs text-gray-500">{med.dosage}</div>
            )}
          </div>
        )}
        onSelect={(item) => {
          setReminderFor(getReminderFor(item));
        }}
        placeholder={t("allergies.search_medicine")}
        value={reminderFor}
        onChange={(v) => {
          setReminderFor(v);
        }}
        loading={searchLoading}
        onSearch={searchMedicines}
        className="w-full"
      />
    );
  };

  const reminderDateField = (
    labelKey: string = "THERAPY",
    isSummaryDisplay: boolean = false
  ) => {
    return paintReminderDateField(
      reminderDate,
      setReminderDate,
      addYears(new Date(), DEFAULT_YEARS_AHEAD),
      t,
      labelKey,
      true,
      isSummaryDisplay
    );
  };

  const reminderEndDateField = (isSummaryDisplay: boolean = false) => {
    return paintReminderEndDateField(
      reminderEndDate,
      setReminderEndDate,
      addYears(new Date(), DEFAULT_YEARS_AHEAD),
      t,
      isSummaryDisplay ? "add" : undefined,
      true,
      isSummaryDisplay
    );
  };

  const reminderNotesField = (isSummaryDisplay: boolean = false) => {
    return paintReminderNotesField(
      notes,
      setNotes,
      t,
      undefined,
      isSummaryDisplay
    );
  };

  const reminderTimeField = (isSummaryDisplay: boolean = false) => {
    return paintReminderTimeField(
      firstDosage,
      setFirstDosage,
      reminderDate,
      t,
      undefined,
      undefined,
      isSummaryDisplay
    );
  };

  const isStartInvalid = (): boolean => {
    return !reminderFor;
  };

  // pass a function to check for disable state
  const paintNextButton = (disableCheck: () => boolean) => {
    return (
      <MButton
        variant="filled"
        size="lg"
        onClick={() => nextStage()}
        className={styles.nextButton}
        disabled={disableCheck()}
      >
        {t("common.next")}
      </MButton>
    );
  };

  const paintDayInputDetails = () => {
    return paintFrequencyDaysField(
      frequencyValue,
      setFrequencyValue,
      t,
      "medication"
    );
  };

  const isDayInputInvalid = (): boolean => {
    return !frequencyValue || frequencyValue < 1;
  };

  const isReminderDetailsInvalid = (): boolean => {
    if (!reminderDate) {
      return true;
    }
    if (reminderEndDate && isBefore(reminderEndDate, reminderDate)) {
      return true;
    }
    // Reminder time is mandatory when dayFrequency is ONCE
    if (dayFrequency === DayFrequency.ONCE && !firstDosage) {
      return true;
    }
    return false;
  };

  const isReminderScheduleInvalid = (): boolean => {
    switch (dayFrequency) {
      case DayFrequency.ONCE:
        return false;
      case DayFrequency.TWICE:
        return (
          !firstDosage ||
          !secondDosage ||
          isTimeBefore(secondDosage, firstDosage)
        );
      case DayFrequency.THRICE:
        return (
          !firstDosage ||
          !secondDosage ||
          !thirdDosage ||
          isTimeBefore(secondDosage, firstDosage) ||
          isTimeBefore(thirdDosage, secondDosage)
        );
      case DayFrequency.FOUR_TIMES:
        return (
          !firstDosage ||
          !secondDosage ||
          !thirdDosage ||
          !fourthDosage ||
          isTimeBefore(secondDosage, firstDosage) ||
          isTimeBefore(thirdDosage, secondDosage) ||
          isTimeBefore(fourthDosage, thirdDosage)
        );
      default:
        return true;
    }
  };

  // Effect to initialize additional dosage times when first dosage is set
  useEffect(() => {
    if (firstDosage && stage === ReminderInputStage.REMINDER_SCHEDULE) {
      // Only calculate if we need to set dosages
      if (
        !secondDosage ||
        (dayFrequency === DayFrequency.THRICE && !thirdDosage) ||
        (dayFrequency === DayFrequency.FOUR_TIMES &&
          (!thirdDosage || !fourthDosage))
      ) {
        // Calculate optimal dosage times using the utility function
        const dosages = calculateOptimalDosages(firstDosage, dayFrequency);

        // Set the calculated dosages if they're not already set
        if (!secondDosage && dosages.secondDosage) {
          setSecondDosage(dosages.secondDosage);
        }

        if (!thirdDosage && dosages.thirdDosage) {
          setThirdDosage(dosages.thirdDosage);
        }

        if (!fourthDosage && dosages.fourthDosage) {
          setFourthDosage(dosages.fourthDosage);
        }
      }
    }
  }, [
    firstDosage,
    stage,
    dayFrequency,
    secondDosage,
    thirdDosage,
    fourthDosage,
  ]);

  const paintReminderSchedules = () => {
    const retArr = [];
    switch (dayFrequency) {
      case DayFrequency.TWICE:
        retArr.push(
          paintDosageInput(firstDosage, setFirstDosage, t, true, "first")
        );
        retArr.push(
          paintDosageInput(secondDosage, setSecondDosage, t, true, "second")
        );
        break;
      case DayFrequency.THRICE:
        retArr.push(
          paintDosageInput(firstDosage, setFirstDosage, t, true, "first")
        );
        retArr.push(
          paintDosageInput(secondDosage, setSecondDosage, t, true, "second")
        );
        retArr.push(
          paintDosageInput(thirdDosage, setThirdDosage, t, true, "third")
        );
        break;
      case DayFrequency.FOUR_TIMES:
        retArr.push(
          paintDosageInput(firstDosage, setFirstDosage, t, true, "first")
        );
        retArr.push(
          paintDosageInput(secondDosage, setSecondDosage, t, true, "second")
        );
        retArr.push(
          paintDosageInput(thirdDosage, setThirdDosage, t, true, "third")
        );
        retArr.push(
          paintDosageInput(fourthDosage, setFourthDosage, t, true, "fourth")
        );
        break;
      default:
        retArr.push(paintDosageInput(firstDosage, setFirstDosage, t, false));
        break;
    }
    return retArr;
  };

  const paintReminderDetails = () => {
    const retArr = [];
    retArr.push(reminderDateField());
    retArr.push(reminderEndDateField());
    if (dayFrequency === DayFrequency.ONCE) {
      retArr.push(reminderTimeField());
    }
    retArr.push(reminderNotesField());
    return retArr;
  };

  const paintFrequencyDetails = () => {
    const retArr = [];
    retArr.push(
      paintDropDownFrequencySelectField(
        frequency,
        setFrequency,
        showFrequencyDrawer,
        setShowFrequencyDrawer,
        t,
        undefined,
        // Medication does not support one-off; therapy-only flow.
        Object.values(ReminderFrequency).filter(
          (f) => f !== ReminderFrequency.ONLY_ONCE
        )
      )
    );
    retArr.push(
      paintDayFrequencyField(
        dayFrequency,
        setDayFrequency,
        showDayFrequencyDrawer,
        setShowDayFrequencyDrawer,
        t
      )
    );
    return retArr;
  };

  const isFrequencyDetailsInvalid = (): boolean => {
    return !frequency || !dayFrequency;
  };

  const paintDayFrequencyInputs = () => {
    const retArr = [];
    retArr.push(
      paintFrequencySpecificDaysField(
        selectedDays,
        setShowSetDays,
        showSetDays,
        handleSetDaysSelect,
        t,
        "medication"
      )
    );
    return retArr;
  };

  const handleSetDaysSelect = (options: string[]) => {
    setSelectedDays(options);
    setShowSetDays(false);
  };

  const isDayFrequencyInvalid = (): boolean => {
    return !dayFrequency;
  };

  const renderMedicationDetails = () => {
    if (!reminderFor) return null;

    const frequencyText = getMedicationFrequencyText(
      frequency,
      selectedDays,
      frequencyValue,
      dayFrequency,
      t
    );

    return (
      <div className="flex gap-[12px]">
        <IconPillFilled
          size={24}
          className="mt-[4px]"
          color="var(--brand-colours-indigo-indigo-500, #8764BB)"
        />
        <div className="flex flex-col gap-[6px]">
          <span
            className="font-semibold leading-[140%]"
            style={{ color: "#36116D" }}
          >
            {reminderFor}
          </span>
          {frequencyText && (
            <div className="flex items-center gap-1 mt-1">
              <IconFrequency size={12} color="#2A2A2A" />
              <span className="text-xs font-[700]" style={{ color: "#2A2A2A" }}>
                {frequencyText}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const paintSummary = () => {
    const summary = [];
    summary.push(reminderDateField("medication.add", true));
    summary.push(reminderEndDateField(true));
    switch (dayFrequency) {
      case DayFrequency.ONCE:
        summary.push(
          paintDosageInput(
            firstDosage,
            setFirstDosage,
            t,
            true,
            "add",
            false,
            true
          )
        );
        break;
      case DayFrequency.TWICE:
        summary.push(
          paintDosageInput(
            firstDosage,
            setFirstDosage,
            t,
            true,
            "firstAdd",
            true,
            true
          )
        );
        summary.push(
          paintDosageInput(
            secondDosage,
            setSecondDosage,
            t,
            true,
            "secondAdd",
            true,
            true
          )
        );
        break;
      case DayFrequency.THRICE:
        summary.push(
          paintDosageInput(
            firstDosage,
            setFirstDosage,
            t,
            true,
            "firstAdd",
            true,
            true
          )
        );
        summary.push(
          paintDosageInput(
            secondDosage,
            setSecondDosage,
            t,
            true,
            "secondAdd",
            true,
            true
          )
        );
        summary.push(
          paintDosageInput(
            thirdDosage,
            setThirdDosage,
            t,
            true,
            "thirdAdd",
            true,
            true
          )
        );
        break;
      case DayFrequency.FOUR_TIMES:
        summary.push(
          paintDosageInput(
            firstDosage,
            setFirstDosage,
            t,
            true,
            "firstAdd",
            true,
            true
          )
        );
        summary.push(
          paintDosageInput(
            secondDosage,
            setSecondDosage,
            t,
            true,
            "secondAdd",
            true,
            true
          )
        );
        summary.push(
          paintDosageInput(
            thirdDosage,
            setThirdDosage,
            t,
            true,
            "thirdAdd",
            true,
            true
          )
        );
        summary.push(
          paintDosageInput(
            fourthDosage,
            setFourthDosage,
            t,
            true,
            "fourthAdd",
            true,
            true
          )
        );
        break;
    }

    summary.push(reminderNotesField(true));
    return summary;
  };

  const isAddInvalid = (): boolean => {
    return isReminderDetailsInvalid() || isReminderScheduleInvalid();
  };

  const paintHeader = (
    title: string,
    description?: string,
    addSpacer: boolean = true
  ) => {
    return (
      <>
        <MHeader
          module="reminders"
          memberInfo={memberInfo}
          title={title}
          description={description ? description : undefined}
          useParentGradient={false}
          className={
            isFinalStage
              ? headerStyles.stickyBelowTopBarWithGradient
              : headerStyles.stickyBelowTopBar
          }
        />
        {addSpacer && <MSpacer />}
      </>
    );
  };

  const handleCreateUpdateReminder = async () => {
    const firstDosageValue = firstDosage || DEFAULT_TIME_12HR;
    const startDate = updateTime(reminderDate, firstDosageValue);
    const endDate = reminderEndDate
      ? updateTime(reminderEndDate, firstDosageValue)
      : null;

    const timezone = getTimezone();
    // Create base reminder data
    const baseReminderData = {
      type: ReminderType.MEDICATION,
      startDate,
      endDate,
      reminderFor,
      frequency,
      notes,
      timezone,
      tier: isCritical ? ("CRITICAL" as const) : ("ROUTINE" as const),
    };

    // Add frequency-specific properties based on the frequency type
    const reminderData: any = { ...baseReminderData };

    if (frequency === ReminderFrequency.DAY_FREQUENCY) {
      reminderData.frequencyValue = frequencyValue;
    } else if (frequency === ReminderFrequency.SPECIFIC_DAYS) {
      reminderData.selectedDays = selectedDays.map((day) => Number(day));
    }

    reminderData.dayFrequency = dayFrequency;
    switch (dayFrequency) {
      case DayFrequency.ONCE:
        reminderData.dayFrequencyValue = [firstDosageValue];
        break;
      case DayFrequency.TWICE:
        reminderData.dayFrequencyValue = [firstDosageValue, secondDosage];
        break;
      case DayFrequency.THRICE:
        reminderData.dayFrequencyValue = [
          firstDosageValue,
          secondDosage,
          thirdDosage,
        ];
        break;
      case DayFrequency.FOUR_TIMES:
        reminderData.dayFrequencyValue = [
          firstDosageValue,
          secondDosage,
          thirdDosage,
          fourthDosage,
        ];
        break;
    }

    if (isEditing && reminder?.id) {
      // Update existing reminder
      const updatedReminder: UpdateReminder = reminderData;
      await updateReminder(reminder.id, updatedReminder);
    } else {
      // Check if this is first reminder BEFORE adding
      const isFirstReminder = totalCount === 0;

      try {
        // Create new reminder
        const newReminder: CreateReminder = reminderData;
        await addReminder(newReminder);

        // Track reminder completion with first reminder flag
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_COMPLETE,
          ...(isFirstReminder && { action: "first_reminder_added" }),
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, "Failed to create reminder");
        showErrorToast(errorMessage);
        return;
      }
    }

    // Track reminder completion for edit case
    if (isEditing) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_MEDICATION_COMPLETE,
      });
    }

    navigate("/reminders");
  };

  // Show loader while fetching medications
  if (medications.length === 0 && loading) {
    return (
      <div className="flex flex-col min-h-screen relative">
        <MTopBar
          hideProfilePicker
          exitPath={exitPath}
          onBack={handleBack}
          withBackground
        />
        <div className="profile-page-root flex items-center justify-center">
          <MLoader />
        </div>
      </div>
    );
  }

  const isFinalStage = stage === ReminderInputStage.ADD || isEditing;

  // If editing, show a simplified view with just the edit form
  if (isEditing) {
    return (
      <div
        className={`flex flex-col min-h-screen relative ${
          isFinalStage ? "pb-[150px]" : "pb-[24px]"
        } ${styles.addStageBackgroundFinal}`}
      >
        <div className="sticky top-0 z-[31]">
          <MTopBar
            hideProfilePicker
            exitPath="/reminders"
            onBack={handleBack}
            withBackground={!isFinalStage}
            showPurpleGradient={isFinalStage}
          />
        </div>
        <div
          className="profile-page-root flex flex-col"
          style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
        >
          <div className={styles.content}>
            <div className="relative w-full h-full">
              {paintHeader(
                t("reminders.medication.addPrompt"),
                t("reminders.medication.addDescription"),
                false
              )}
              <div className="profile-input-container ">
                <MFrame className="items-start py-6 px-5  mb-[-22px]">
                  {renderMedicationDetails()}
                </MFrame>
                <MFrame className="">
                  <div
                    className={`w-full flex flex-col gap-[24px] px-[8px] pt-[0px] pb-[8px]`}
                  >
                    {paintSummary()}
                    <CriticalReminderToggle
                      checked={isCritical}
                      onChange={setIsCritical}
                    />
                  </div>
                </MFrame>
              </div>
              <div className="confirm-footer">
                <MButton
                  text={t("reminders.setReminder")}
                  variant="filled"
                  size="lg"
                  onClick={() => handleCreateUpdateReminder()}
                  className="w-full"
                  disabled={isAddInvalid()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular flow for creating a new reminder
  return (
    <div
      className={`flex flex-col min-h-screen relative ${
        isFinalStage ? "pb-[125px]" : "pb-[24px]"
      } ${isFinalStage ? styles.addStageBackgroundFinal : ""}`}
    >
      {isFinalStage ? (
        <div className="sticky top-0 z-[31]">
          <MTopBar
            hideProfilePicker
            exitPath={exitPath}
            onBack={handleBack}
            showPurpleGradient
          />
        </div>
      ) : (
        <MTopBar
          hideProfilePicker
          exitPath={exitPath}
          onBack={handleBack}
          withBackground
        />
      )}
      <div
        className="profile-page-root flex flex-col"
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className={styles.content}>
          {stage === ReminderInputStage.START && (
            <div className={styles.cardContainer}>
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  t("reminders.medication.selectPrompt"),
                  t("reminders.medication.selectDescription")
                )}
                <div className="profile-input-container flex-1">
                  {paintTypeAheadInput()}
                  {paintMedicationsSelect()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(isStartInvalid)}
                </div>
              </div>
            </div>
          )}
          {stage === ReminderInputStage.FREQUENCY_DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.detailsPrompt"),
                t("reminders.medication.detailsDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintFrequencyDetails()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isFrequencyDetailsInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.DAY_FREQUENCY && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.dayFrequencyPrompt"),
                t("reminders.medication.dayFrequencyDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintDayFrequencyInputs()}
              </div>
              <div className="confirm-footer pt-0">
                {paintNextButton(isDayFrequencyInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.DAY_INPUT && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.dayInputPrompt"),
                t("reminders.medication.dayInputDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintDayInputDetails()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isDayInputInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.REMINDER_SCHEDULE && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.reminderSchedulePrompt"),
                t("reminders.medication.reminderScheduleDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintReminderSchedules()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isReminderScheduleInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.REMINDER_DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t(
                  `reminders.medication.reminderDetailsPrompt${
                    dayFrequency !== DayFrequency.ONCE ? ".dateOnly" : ""
                  }`
                ),
                t(
                  `reminders.medication.reminderDetailsDescription${
                    dayFrequency !== DayFrequency.ONCE ? ".dateOnly" : ""
                  }`
                )
              )}
              <div className="profile-input-container flex-1">
                {paintReminderDetails()}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(isReminderDetailsInvalid)}
              </div>
            </div>
          )}
          {stage === ReminderInputStage.ADD && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                t("reminders.medication.addPrompt"),
                t("reminders.medication.addDescription"),
                false
              )}
              <div className="profile-input-container">
                <MFrame className="items-start py-6 px-5  mb-[-22px]">
                  {renderMedicationDetails()}
                </MFrame>
                <MFrame className="">
                  <div
                    className={`w-full flex flex-col gap-[24px] px-[8px] pt-[0px] pb-[8px]`}
                  >
                    {paintSummary()}
                    <CriticalReminderToggle
                      checked={isCritical}
                      onChange={setIsCritical}
                    />
                  </div>
                </MFrame>
              </div>
              <div className="confirm-footer">
                <MButton
                  text={t("reminders.setReminder")}
                  variant="filled"
                  size="lg"
                  onClick={() => handleCreateUpdateReminder()}
                  className="w-full"
                  disabled={isAddInvalid()}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationReminder;
